import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Container,
  Autocomplete,
  MenuItem,
  FormControlLabel,
  Switch,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Science as ChemoIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { createChemotherapyRegimen } from '../../services/chemotherapyService';
import { PageHeader, HeaderButton, PageContainer, ContentCard } from './oncologyStyles';

// Common cancer types for autocomplete
const CANCER_TYPES = [
  'Breast Cancer', 'Lung Cancer (NSCLC)', 'Lung Cancer (SCLC)', 'Colorectal Cancer',
  'Gastric Cancer', 'Pancreatic Cancer', 'Ovarian Cancer', 'Cervical Cancer',
  'Prostate Cancer', 'Bladder Cancer', 'Kidney Cancer', 'Lymphoma (Hodgkin)',
  'Lymphoma (Non-Hodgkin)', 'Multiple Myeloma', 'Leukemia (AML)', 'Leukemia (ALL)',
  'Leukemia (CML)', 'Leukemia (CLL)', 'Head and Neck Cancer', 'Esophageal Cancer',
  'Hepatocellular Carcinoma', 'Melanoma', 'Sarcoma', 'Thyroid Cancer', 'Brain Tumors', 'Testicular Cancer'
];

// Common pre-chemo labs
const PRE_CHEMO_LABS = [
  'CBC with differential', 'CMP', 'LFTs', 'BUN/Creatinine', 'Electrolytes',
  'Magnesium', 'Phosphorus', 'Uric acid', 'PT/INR', 'PTT', 'Cardiac markers',
  'Tumor markers', 'Urinalysis', 'Pregnancy test', 'HIV', 'Hepatitis B', 'Hepatitis C'
];

const ROUTES = ['IV Push', 'IV Infusion', 'IV Bolus', 'Subcutaneous', 'Intramuscular', 'Oral', 'Intrathecal', 'Intraperitoneal'];
const DOSE_UNITS = ['mg/m²', 'mg/kg', 'mg', 'AUC', 'units/m²', 'units', 'mcg/m²', 'mcg/kg', 'mcg'];

const emptyDrug = {
  drugName: '', standardDose: '', doseUnit: 'mg/m²', route: 'IV Infusion',
  scheduleDays: [], infusionDuration: '', sequence: 1, premedications: '', notes: ''
};

const ChemotherapyRegimenForm = () => {
  const navigate = useNavigate();
  const { defaultFacility, currentUser } = useUserData();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: '', code: '', description: '', cycleLengthDays: 21, numberOfCycles: 6,
    cancerTypes: [], drugs: [{ ...emptyDrug }], preChemoLabs: ['CBC with differential', 'CMP'],
    notes: '', isActive: true, emetogenicRisk: 'Moderate', requiresBSA: true
  });

  const [dayInput, setDayInput] = useState({});

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  
  const handleDrugChange = (index, field, value) => {
    const updatedDrugs = [...formData.drugs];
    updatedDrugs[index] = { ...updatedDrugs[index], [field]: value };
    setFormData(prev => ({ ...prev, drugs: updatedDrugs }));
  };

  const addDrug = () => setFormData(prev => ({
    ...prev, drugs: [...prev.drugs, { ...emptyDrug, sequence: prev.drugs.length + 1 }]
  }));

  const removeDrug = (index) => {
    if (formData.drugs.length <= 1) return;
    const updatedDrugs = formData.drugs.filter((_, i) => i !== index);
    updatedDrugs.forEach((drug, i) => { drug.sequence = i + 1; });
    setFormData(prev => ({ ...prev, drugs: updatedDrugs }));
  };

  const duplicateDrug = (index) => {
    const drugToCopy = { ...formData.drugs[index], sequence: formData.drugs.length + 1 };
    setFormData(prev => ({ ...prev, drugs: [...prev.drugs, drugToCopy] }));
  };

  const handleDayInputChange = (drugIndex, value) => setDayInput(prev => ({ ...prev, [drugIndex]: value }));

  const addScheduleDay = (drugIndex) => {
    const dayValue = parseInt(dayInput[drugIndex]);
    if (!isNaN(dayValue) && dayValue > 0 && dayValue <= formData.cycleLengthDays) {
      const currentDays = formData.drugs[drugIndex].scheduleDays || [];
      if (!currentDays.includes(dayValue)) {
        handleDrugChange(drugIndex, 'scheduleDays', [...currentDays, dayValue].sort((a, b) => a - b));
      }
    }
    setDayInput(prev => ({ ...prev, [drugIndex]: '' }));
  };

  const removeScheduleDay = (drugIndex, day) => {
    handleDrugChange(drugIndex, 'scheduleDays', (formData.drugs[drugIndex].scheduleDays || []).filter(d => d !== day));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { setError('Regimen name is required'); return; }
    if (!formData.code.trim()) { setError('Regimen code is required'); return; }
    if (formData.drugs.length === 0 || !formData.drugs[0].drugName.trim()) { setError('At least one drug is required'); return; }
    if (formData.cancerTypes.length === 0) { setError('At least one cancer type must be selected'); return; }
    for (let i = 0; i < formData.drugs.length; i++) {
      if (!formData.drugs[i].scheduleDays?.length) {
        setError(`Drug ${i + 1} (${formData.drugs[i].drugName || 'unnamed'}) needs schedule days`);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      await createChemotherapyRegimen({ ...formData, facilityId: defaultFacility.id, createdBy: currentUser?.uid });
      setSuccess(true);
      setTimeout(() => navigate('/oncology/chemotherapy'), 1500);
    } catch (err) {
      console.error('Error creating regimen:', err);
      setError(err.message || 'Failed to create chemotherapy regimen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title="Add Custom Regimen"
          subtitle="Create a new chemotherapy protocol"
          icon={<ChemoIcon />}
          backTo="/oncology/chemotherapy"
          actions={
            <HeaderButton
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Regimen'}
            </HeaderButton>
          }
        />

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Chemotherapy regimen created successfully! Redirecting...</Alert>}

        {/* Basic Information */}
        <ContentCard sx={{ mb: 2 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>Basic Information</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" label="Regimen Name *" placeholder="e.g., FOLFOX6"
                  value={formData.name} onChange={(e) => handleChange('name', e.target.value)} />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth size="small" label="Code *" placeholder="e.g., mFOLFOX6"
                  value={formData.code} onChange={(e) => handleChange('code', e.target.value.toUpperCase())} />
              </Grid>
              <Grid item xs={4} md={2}>
                <TextField fullWidth size="small" label="Cycle (days)" type="number"
                  value={formData.cycleLengthDays} onChange={(e) => handleChange('cycleLengthDays', parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1, max: 90 }} />
              </Grid>
              <Grid item xs={4} md={2}>
                <TextField fullWidth size="small" label="# Cycles" type="number"
                  value={formData.numberOfCycles} onChange={(e) => handleChange('numberOfCycles', parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1, max: 24 }} />
              </Grid>
              <Grid item xs={4} md={3}>
                <TextField fullWidth size="small" label="Emetogenic Risk" select
                  value={formData.emetogenicRisk} onChange={(e) => handleChange('emetogenicRisk', e.target.value)}>
                  <MenuItem value="Minimal">Minimal</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Moderate">Moderate</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={8}>
                <Autocomplete
                  multiple size="small" options={CANCER_TYPES} value={formData.cancerTypes}
                  onChange={(_, newValue) => handleChange('cancerTypes', newValue)} freeSolo
                  renderTags={(value, getTagProps) => value.map((option, index) => (
                    <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                  ))}
                  renderInput={(params) => <TextField {...params} label="Cancer Types *" placeholder="Select..." />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%' }}>
                  <FormControlLabel
                    control={<Switch size="small" checked={formData.requiresBSA} onChange={(e) => handleChange('requiresBSA', e.target.checked)} />}
                    label={<Typography variant="body2">BSA dosing</Typography>}
                  />
                  <FormControlLabel
                    control={<Switch size="small" checked={formData.isActive} onChange={(e) => handleChange('isActive', e.target.checked)} />}
                    label={<Typography variant="body2">Active</Typography>}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </ContentCard>

        {/* Drugs Section */}
        <ContentCard sx={{ mb: 2 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold">Drugs in Protocol</Typography>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addDrug}>Add Drug</Button>
            </Box>

            {formData.drugs.map((drug, index) => (
              <Card key={index} variant="outlined" sx={{ mb: 1.5, borderRadius: '8px', bgcolor: 'grey.50' }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold" color="primary">Drug #{drug.sequence}</Typography>
                    <Box>
                      <Tooltip title="Duplicate"><IconButton size="small" onClick={() => duplicateDrug(index)}><CopyIcon fontSize="small" /></IconButton></Tooltip>
                      {formData.drugs.length > 1 && (
                        <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => removeDrug(index)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={4} md={3}>
                      <TextField fullWidth size="small" label="Drug Name *" placeholder="Oxaliplatin"
                        value={drug.drugName} onChange={(e) => handleDrugChange(index, 'drugName', e.target.value)} />
                    </Grid>
                    <Grid item xs={3} sm={2} md={1.5}>
                      <TextField fullWidth size="small" label="Dose" placeholder="85"
                        value={drug.standardDose} onChange={(e) => handleDrugChange(index, 'standardDose', e.target.value)} />
                    </Grid>
                    <Grid item xs={3} sm={2} md={1.5}>
                      <TextField fullWidth size="small" label="Unit" select value={drug.doseUnit}
                        onChange={(e) => handleDrugChange(index, 'doseUnit', e.target.value)}>
                        {DOSE_UNITS.map(unit => <MenuItem key={unit} value={unit}>{unit}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={4} sm={2} md={2}>
                      <TextField fullWidth size="small" label="Route" select value={drug.route}
                        onChange={(e) => handleDrugChange(index, 'route', e.target.value)}>
                        {ROUTES.map(route => <MenuItem key={route} value={route}>{route}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={4} sm={2} md={1.5}>
                      <TextField fullWidth size="small" label="Duration" placeholder="2h"
                        value={drug.infusionDuration} onChange={(e) => handleDrugChange(index, 'infusionDuration', e.target.value)} />
                    </Grid>
                    <Grid item xs={4} sm={12} md={2.5}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Days (1-{formData.cycleLengthDays})</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                          {drug.scheduleDays?.map(day => (
                            <Chip key={day} label={day} size="small" onDelete={() => removeScheduleDay(index, day)} sx={{ height: 22 }} />
                          ))}
                          <TextField
                            size="small" type="number" placeholder="+" value={dayInput[index] || ''}
                            onChange={(e) => handleDayInputChange(index, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addScheduleDay(index); }}}
                            inputProps={{ min: 1, max: formData.cycleLengthDays }}
                            sx={{ width: '50px', '& input': { py: 0.5, px: 1, textAlign: 'center' } }}
                          />
                          <IconButton size="small" onClick={() => addScheduleDay(index)} color="primary" sx={{ p: 0.25 }}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Premedications" placeholder="Ondansetron 8mg IV..."
                        value={drug.premedications} onChange={(e) => handleDrugChange(index, 'premedications', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Notes" placeholder="Special instructions..."
                        value={drug.notes} onChange={(e) => handleDrugChange(index, 'notes', e.target.value)} />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </ContentCard>

        {/* Pre-Chemo Labs & Notes */}
        <ContentCard sx={{ mb: 2 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>Pre-Chemo Requirements & Notes</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple size="small" options={PRE_CHEMO_LABS} value={formData.preChemoLabs}
                  onChange={(_, newValue) => handleChange('preChemoLabs', newValue)} freeSolo
                  renderTags={(value, getTagProps) => value.map((option, index) => (
                    <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                  ))}
                  renderInput={(params) => <TextField {...params} label="Pre-Chemo Labs" placeholder="Select..." />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Additional Notes" placeholder="Additional instructions..."
                  value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} multiline rows={1} />
              </Grid>
            </Grid>
          </CardContent>
        </ContentCard>

        {/* Bottom Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 3 }}>
          <Button size="small" variant="outlined" onClick={() => navigate('/oncology/chemotherapy')}>Cancel</Button>
          <Button
            size="small" variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit} disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Regimen'}
          </Button>
        </Box>
      </Container>
    </PageContainer>
  );
};

export default ChemotherapyRegimenForm;
